import axios from 'axios'
import { defineStore } from 'pinia'
import { useStore } from 'vuex'
import { castAttributesForSnakeCase, castAttributesForCamelCase } from '../../../modules/string_converter'

const store = useStore()

export interface State {
  targetPeriodLoading: boolean,
  comparedPeriodLoading: boolean,
  averageDataLoading: boolean,
  targetPeriodData: SurveysDetailData
  targetPeriodMetaData: SurveysDetailMetaData
  comparePeriodData: SurveysDetailData
  averageData: SurveysDetailData
}

export type SurveysDetailData = {
  fields: Fields[]
  questions: Question[]
  answersCount: number
  storeNames: string[]
}
export type SurveysDetailMetaData = {
  questions: MetaQuestion[]
}

export interface Fields {
  key: string
  id: string
  name: string
}

export interface NpsCountValue {
  value: number
  rate: number
}

export interface NpsValue {
  detractors: NpsCountValue
  passives: NpsCountValue
  promoters: NpsCountValue
  score: number
}

type TextMetaQuestion = {
  id: number
  name: string
  type: 'text' | 'one_line_text'
  position: number
}
type RadioMetaQuestion = {
  id: number
  name: string
  type: 'radio'
  position: number
  options: MetaOption[]
}

export type CheckboxMetaQuestion = {
  id: number
  name: string
  type: 'checkbox'
  position: number
  options: MetaOption[]
}

type NpsMetaQuestion = {
  id: number
  name: string
  type: 'nps'
  position: number
}

type TextQuestion = {
  id: number
  type: 'text' | 'one_line_text'
  texts: string[]
}

export interface RadioQuestionOption {
  id: number
  value: number
  rate: number
}

type RadioQuestion = {
  id: number
  type: 'radio'
  options: RadioQuestionOption[]
}

export interface CheckboxQuestionOption {
  id: number
  value: number
}

export type CheckboxQuestion = {
  id: number
  type: 'checkbox'
  options: CheckboxQuestionOption[]
}

type NpsQuestion = {
  id: number
  type: 'nps'
  value: NpsValue
}
export interface MetaOption {
  id: number
  name: string
}
export type Question = TextQuestion | RadioQuestion | CheckboxQuestion | NpsQuestion
export type MetaQuestion = TextMetaQuestion | RadioMetaQuestion | CheckboxMetaQuestion | NpsMetaQuestion

export type Query = {
  answeredAtTerm: string | string[]
}

export interface PeriodPathParams {
  id: string
  axisType: string
  axisId: string
}

export interface AveragePathParams {
  id: string
  axisType: string
}

export const companiesAnalysisSurveysDetail = defineStore('companiesAnalysisSurveysDetail',{
  state: (): State => {
    return {
      targetPeriodLoading: false,
      comparedPeriodLoading: false,
      averageDataLoading: false,
      targetPeriodData: {
        fields: [],
        questions: [],
        answersCount: 0,
        storeNames: []
      },
      targetPeriodMetaData: {
        questions: [],
      },
      comparePeriodData: {
        fields: [],
        questions: [],
        answersCount: 0,
        storeNames: []
      },
      averageData: {
        fields: [],
        questions: [],
        answersCount: 0,
        storeNames: []
      },
    }
  },
  getters: {},
  actions: {
    readAnalysisCustomSurveysDetail(companyUuid: string, query: Query, pathParams: PeriodPathParams): Promise<void> {
      this.targetPeriodLoading = true
      return axios
        .get('/api/companies/' + companyUuid + '/analysis/custom_surveys/' + pathParams.id + '/aggregates/' + pathParams.axisType + '/' + pathParams.axisId + '.json', {
          params: {
            ...castAttributesForSnakeCase(query),
          }
        })
        .then(res => {
          this.targetPeriodData = castAttributesForCamelCase(res.data.data) as SurveysDetailData
          this.targetPeriodMetaData = castAttributesForCamelCase(res.data.meta) as SurveysDetailMetaData
        })
        .catch(err => {
          store.commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          console.error(err)
        })
        .finally(() => { this.targetPeriodLoading = false })
    },
    readAnalysisCustomSurveysDetailComparedPeriod(companyUuid: string, query: Query, pathParams: PeriodPathParams): Promise<void> {
      this.comparedPeriodLoading = true
      return axios
        .get('/api/companies/' + companyUuid + '/analysis/custom_surveys/' + pathParams.id + '/aggregates/' + pathParams.axisType + '/' + pathParams.axisId + '.json', {
          params: {
            ...castAttributesForSnakeCase(query),
          }
        })
        .then(res => {
          this.comparePeriodData = castAttributesForCamelCase(res.data.data) as SurveysDetailData
        })
        .catch(err => {
          store.commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          console.error(err)
        })
        .finally(() => { this.comparedPeriodLoading = false })
    },
    readAnalysisCustomSurveysDetailAverage(companyUuid: string, query: Query, pathParams: AveragePathParams): Promise<void> {
      this.averageDataLoading = true
      return axios
        .get('/api/companies/' + companyUuid + '/analysis/custom_surveys/' + pathParams.id + '/aggregates/' + pathParams.axisType + '/average' + '.json', {
          params: {
            ...castAttributesForSnakeCase(query),
          }
        })
        .then(res => {
          this.averageData = castAttributesForCamelCase(res.data.data) as SurveysDetailData
        })
        .catch(err => {
          store.commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          console.error(err)
        })
        .finally(() => { this.averageDataLoading = false })
    },
  }
})